import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import PageShell from '../layout/PageShell';
import {
  apiErrorMessage,
  confirmPayoutAddressChange,
  isOrderConfirmation,
  type PayoutChangeConfirmed,
} from '../lib/api';

/**
 * Where an emailed payout-change confirmation link lands.
 *
 * ===========================================================================
 * THE SECOND HALF OF THE PHASE 1 SCOPE LINE.
 *
 * "Secure payout-address change on an active order (email-confirmed)." The customer
 * asked for the change on the order page; this is where it actually happens. Until this
 * page runs, nothing has moved -- not the order, not the entitlement at PowerPool.
 *
 * ===========================================================================
 * IT CONFIRMS ON ARRIVAL, DELIBERATELY, AND THAT IS THE SAME CHOICE /sign-in/verify
 * MAKES.
 *
 * The customer has already made the decision -- twice: once in the form, once by
 * clicking a link that says what it does. A second "are you sure?" button here would
 * add a step without adding a decision.
 *
 * The token is single-use, so the effect runs ONCE. React 18 Strict Mode mounts effects
 * twice in development, and a second call would spend the link and report failure for a
 * change that actually succeeded -- so a ref guards it, exactly as the sign-in page does.
 *
 * ===========================================================================
 * NO SESSION IS INVOLVED, EITHER WAY.
 *
 * The page does not require one: the customer may well open the link on the device their
 * email is on rather than the one they were signed in on. And confirming does not CREATE
 * one -- a payout-change link must never work as a sign-in link, which is why these
 * tokens live in their own table with their own lifetime.
 */
export default function ConfirmPayoutChange() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  /*
   * THE WHOLE RESULT IS KEPT, not two fields picked out of it.
   *
   * A link can confirm an ORDER change or an ACCOUNT-DEFAULT change, and the server
   * answers with two different shapes. Lifting `orderNumber`/`payoutAddress` out here
   * silently produced `undefined` for the account-default shape -- which is how this
   * page came to render "Order now pays to ." with both values blank.
   */
  const [state, setState] = useState<
    | { status: 'confirming' }
    | { status: 'done'; result: PayoutChangeConfirmed }
    | { status: 'failed'; reason: string }
  >({ status: 'confirming' });

  // Strict Mode mounts effects twice. The token is single-use; the second call would
  // spend nothing and report a failure for a change that worked.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setState({
        status: 'failed',
        reason: 'That link is missing its confirmation code. Please use the link from your email.',
      });
      return;
    }

    confirmPayoutAddressChange(token)
      .then((result) => setState({ status: 'done', result }))
      .catch((caught: unknown) =>
        setState({
          status: 'failed',
          // The server's own safe sentence. It never carries provider detail, and it
          // deliberately does not say WHICH of expired, used or unknown applied.
          reason: apiErrorMessage(
            caught,
            'That confirmation link is invalid or has expired. Please request the change again.',
          ),
        }),
      );
  }, [token]);

  return (
    <PageShell width="mm-review-shell mx-auto w-full max-w-2xl px-6 py-10">
      {state.status === 'confirming' && (
        <>
          <p className="mm-eyebrow">Payout address</p>
          <h1 className="mm-display mt-3" data-testid="confirm-payout-title">
            Confirming your change
          </h1>
          <p className="mm-lede mt-4" data-testid="confirm-payout-pending">
            One moment.
          </p>
        </>
      )}

      {/*
        ONE SENTENCE PER SCOPE, because the two changes are not the same fact.

        An order confirmation moved ONE rental and can link to it. An account-default
        confirmation saved the wallet and moved every active rental -- there is no single
        order to name or link to, and saying "Order ... now pays to" would be wrong even
        with the values filled in.
      */}
      {state.status === 'done' && isOrderConfirmation(state.result) && (
        <>
          <p className="mm-eyebrow">Payout address</p>
          <h1 className="mm-display mt-3" data-testid="confirm-payout-title">
            Payout address updated
          </h1>
          <p className="mm-lede mt-4" data-testid="confirm-payout-summary">
            Order {state.result.orderNumber} now pays to{' '}
            <span className="mm-numeric font-medium">{state.result.payoutAddress}</span>.
            Future block rewards for this rental go to that address.
          </p>

          <Link
            to={`/orders/${encodeURIComponent(state.result.orderNumber)}`}
            className="mm-btn-secondary mt-6 inline-flex"
            data-testid="confirm-payout-order-link"
          >
            View your order
          </Link>
        </>
      )}

      {state.status === 'done' && !isOrderConfirmation(state.result) && (
        <>
          <p className="mm-eyebrow">Payout address</p>
          <h1 className="mm-display mt-3" data-testid="confirm-payout-title">
            Payout wallet updated
          </h1>
          <p className="mm-lede mt-4" data-testid="confirm-payout-summary">
            Your payout wallet is now{' '}
            <span className="mm-numeric font-medium">
              {state.result.defaultPayoutAddress}
            </span>
            . New orders will use it by default.
          </p>

          {/*
            THE COUNTS ONLY WHEN THEY MEAN SOMETHING. A customer with nothing running
            should not read "0 of 0 active orders were updated" -- the same rule the
            account page applies.
          */}
          {state.result.eligibleCount > 0 && (
            <p className="mm-help mt-3" data-testid="confirm-payout-fanout">
              {state.result.failedCount === 0
                ? `${state.result.updatedCount} active ${
                    state.result.updatedCount === 1 ? 'order was' : 'orders were'
                  } moved to it.`
                : `${state.result.updatedCount} of ${state.result.eligibleCount} active orders were moved to it. ${
                    state.result.failedCount
                  } ${
                    state.result.failedCount === 1 ? 'order still uses' : 'orders still use'
                  } its previous payout address.`}
            </p>
          )}

          <Link to="/orders" className="mm-btn-secondary mt-6 inline-flex">
            View your orders
          </Link>
        </>
      )}

      {state.status === 'failed' && (
        <>
          <p className="mm-eyebrow">Payout address</p>
          <h1 className="mm-display mt-3" data-testid="confirm-payout-title">
            We couldn&rsquo;t confirm that change
          </h1>

          <p role="alert" className="mm-note mm-note--danger mt-6" data-testid="confirm-payout-error">
            <span>{state.reason}</span>
          </p>

          {/* The reassurance that matters: a failed confirmation changed nothing. */}
          <p className="mm-help mt-4">
            Your payout address has not changed. Open your order and request the change
            again to get a new link.
          </p>

          <Link to="/orders" className="mm-btn-secondary mt-6 inline-flex">
            Back to your orders
          </Link>
        </>
      )}
    </PageShell>
  );
}
